export const OBSERVATION_EVIDENCE_FIELD_TYPES = [
    'radio',
    'checkbox-group',
    'range',
];

export const RANGE_RECOMMENDATION_THRESHOLD = 80;

export function isObservationEvidenceFieldType(type) {
    return OBSERVATION_EVIDENCE_FIELD_TYPES.includes(type);
}

export function isObservationEvidenceField(field) {
    if (!field || typeof field !== 'object') return false;
    if (field.observationAndRecommendation !== true) return false;
    const maxScore = Number(field.max_score);
    if (!Number.isFinite(maxScore) || maxScore <= 0) return false;
    return isObservationEvidenceFieldType(field.type);
}

export function getNumericRangeValue(field) {
    const raw = field?.value;
    if (raw === '' || raw === null || raw === undefined) {
        return NaN;
    }
    const scaleMax = Number(field?.params?.max ?? 100);
    const max = Number.isFinite(scaleMax) && scaleMax > 0 ? scaleMax : 100;
    const num = Number(raw);
    if (!Number.isFinite(num)) {
        return NaN;
    }
    return Math.min(Math.max(num, 0), max);
}

function getChoiceFieldSelections(field) {
    if (field?.type === 'checkbox-group') {
        return Array.isArray(field?.value) ? field.value : [];
    }
    if (field?.type === 'radio') {
        return field?.value != null && field.value !== '' ? [field.value] : [];
    }
    return [];
}

export function hasObservationEvidenceSelection(field) {
    if (!field || typeof field !== 'object') return false;

    if (field.type === 'range') {
        return Number.isFinite(getNumericRangeValue(field));
    }

    if (field.type === 'radio' || field.type === 'checkbox-group') {
        return getChoiceFieldSelections(field).length > 0;
    }

    return false;
}

function isChoiceRecommendationRequired(field) {
    const rules = Array.isArray(field?.scoring_rules)
        ? field.scoring_rules
        : [];
    if (rules.length === 0) return false;

    const options = Array.isArray(field?.params?.options)
        ? field.params.options
        : [];
    const naValues = new Set(
        options.filter((opt) => opt?.isNA).map((opt) => opt.value)
    );

    const selectedScoredValues = getChoiceFieldSelections(field).filter(
        (v) => !naValues.has(v)
    );
    if (selectedScoredValues.length === 0) return false;

    return selectedScoredValues.some((v) => !rules.includes(v));
}

export function isRecommendationRequired(field) {
    if (!isObservationEvidenceField(field)) return false;
    if (!hasObservationEvidenceSelection(field)) return false;

    if (field.type === 'range') {
        const value = getNumericRangeValue(field);
        return (
            Number.isFinite(value) && value < RANGE_RECOMMENDATION_THRESHOLD
        );
    }

    return isChoiceRecommendationRequired(field);
}

export function shouldShowRecommendation(field) {
    if (!isObservationEvidenceField(field)) return false;
    if (!hasObservationEvidenceSelection(field)) return false;

    if (field.type === 'range') {
        return true;
    }

    return isChoiceRecommendationRequired(field);
}

export function isRangeAuditGap(field) {
    if (!field || field.type !== 'range') return false;
    if (!isObservationEvidenceField(field)) return false;

    const recommendation =
        field?.recommendation != null ? String(field.recommendation).trim() : '';

    return recommendation.length > 0;
}

export function includesObservationEvidenceDefaults(field) {
    return (
        isObservationEvidenceField(field) &&
        (field.type === 'range' ||
            Array.isArray(field?.params?.options))
    );
}
